import { defineComponent as _defineComponent } from 'vue'
import { type PropType, computed, nextTick, onMounted, ref, useStore, watch } from "@nuxtjs/composition-api";

import { useVuetify } from "@/composables";
import { getLtksStore } from "@/store/ltks";
import { type Masonry, type MasonryColumnItem } from "@/types";
import {
  type LtkItemHeightCalculation,
  heroImageOnly,
  withProductsRow,
} from "@/utils/services/LtkItemHeightCalculator";
import { sortByNumberAscending } from "@/utils/transform/array";


export default /*#__PURE__*/_defineComponent({
  __name: 'LtkListVirtual',
  props: {
  /**
   * Array of ltk id strings, and the relative order they should be rendered
   *
   * @required
   */
  ltkIds: {
    type: Array as PropType<string[]>,
    required: true,
  },
  /**
   * Number of items from the end of the shortest column a pagination trigger willbe positioned.
   * When set to -1, this will disable placement of the pagination item.
   *
   * @default -1
   */
  itemsBeforePage: {
    type: Number,
    default: -1,
  },
  /**
   * Spacing in pixels between list items on the vertical axis (virtual only)
   */
  itemVerticalSpacing: {
    type: Number,
    default: 0,
  },
  /**
   * Spacing in pixels between list items on the horizontal axis (virtual only)
   */
  itemHorizontalSpacing: {
    type: Number,
    default: 0,
  },
  /**
   * Number of masonry columns to render.
   */
  columnCount: {
    type: Number,
    default: 2,
  },
  itemHeightFunction: {
    type: Function as PropType<LtkItemHeightCalculation>,
    required: true,
  },
  isVisible: {
    type: Boolean,
    default: false,
  },
},
  setup(__props) {

const props = __props;



const store = useStore();
const { breakpoint } = useVuetify();
const root = ref<HTMLElement | null>(null);

const iPhoneXHeight = 812;
const iPhoneXWidth = 164;
const resizeDelayOnTransition = 100;

/**
 * Reactive prop for tracking if hydration has been done to this component. Useful to avoid unnecessary
 * rendering of the SSR placeholder template. Fixes performance issues of initial page render when a
 * large number of ltkIds exist for a profile, since it will create the subcomponent tree multiple times.
 */
const hasHydrated = ref(false);

/**
 * Current scroll height in the page
 */
const scrollHeight = ref(0);

/**
 * Current width of a single column. Defaults to the calculated width on an iphone x
 */
const columnWidth = ref(iPhoneXWidth);

/**
 * Current height of the viewport. Defaults to the screen height of an iphone x
 */
const viewportHeight = ref(iPhoneXHeight);

onMounted(async () => {
  // TODO: Remove this once SSR CSS prerendering issues have been resolved.
  // Allow layout shifts after vuetify css has been loaded after SSR
  await nextTick();
  handleScroll();
  handleResize();

  // Prevent future templating of the client-only placeholder slot
  hasHydrated.value = true;
});

/**
 * Reacts to the current scroll height, and returns only the subsets of each column that are within the viewport
 * range. When rendering server side, or without being set to a virtual scroll, the full masonry layout is
 * instead returned.
 */
const virtualMasonry = computed(() => {
  const parentTop = root.value?.offsetTop ?? 0;

  /**
   * Return true when any part of the item is between the viewport + padding.
   *
   * Using the current viewport height as an additional page buffer on either side of the scroll
   */
  const filterItemsBetweenViewport = (item: MasonryColumnItem): boolean =>
    // Is the bottom of the item below the top of the viewport + padding
    // item Bottom > viewportTop
    item.offset + item.height > scrollHeight.value - parentTop - viewportHeight.value &&
    // Is the top of the item above the bottom of the viewport + padding
    // item top < viewportBottom
    item.offset < scrollHeight.value - parentTop + viewportHeight.value * 2;

  return flatMasonry.value.filter(filterItemsBetweenViewport);
});

const flatMasonry = computed(() =>
  masonry.value.columns
    .flatMap((column) => column.items)
    .sort((a, b) => sortByNumberAscending(a.originalIndex, b.originalIndex)),
);

const shortestColumnFunc = (masonry: Masonry) => {
  let shortestColumn = 0;
  let tallestColumn = 0;
  for (let x = 0; x < masonry.columns.length; x++) {
    if (masonry.columns[x].height < masonry.columns[shortestColumn].height) {
      shortestColumn = x;
    }
    if (masonry.columns[x].height > masonry.columns[tallestColumn].height) {
      tallestColumn = x;
    }
  }

  return [shortestColumn, tallestColumn];
};

/**
 * Reacts to the list of ltkIds provided, sorts the ids into columns following a masonry style layout. Starting from
 * the first column, items will be added to the shortest calculated column. This does employ a slightly custom
 * calculation of height to be responsive without needing events sent from child components to constantly
 * recalculate the positioning. The item.offset is used only in when set to virtual scroll where there are no
 * previous items to do the positioning natively.
 */
const masonry = computed(() => {
  const masonry: Masonry = { columns: [], shortestColumn: 0, tallestColumn: 0 };

  for (let x = props.columnCount; x > 0; x--) {
    masonry.columns.push({
      items: [],
      height: 0,
    });
  }

  // Use for-loops for small performance gains over .forEach or .reduce. Especially when item counts begin to get larger
  for (let x = 0; x < props.ltkIds.length; x++) {
    const [shortestColumn] = shortestColumnFunc(masonry);
    const ltkId = props.ltkIds[x];

    /*
      If itemHeightFunction is "withProductsRow", check if it has products, if it doesn't use "heroImageOnly"
    */
    const hasProducts = (getLtksStore(store).ltks[ltkId]?.productIds?.length ?? 0) > 0;
    let heightFunction = props.itemHeightFunction;
    if (heightFunction === withProductsRow && !hasProducts) {
      heightFunction = heroImageOnly;
    }

    const height = heightFunction(store, { ltkId, width: columnWidth.value }) + props.itemVerticalSpacing;
    const column = masonry.columns[shortestColumn];

    // Setting the current offset to the previous height. This item should get created first
    column.items.push({
      id: ltkId,
      height,
      index: column.items.length,
      offset: column.height,
      originalIndex: x,
      columnNumber: shortestColumn,
    });
    column.height += height;
  }

  const [shortestColumn, tallestColumn] = shortestColumnFunc(masonry);
  masonry.shortestColumn = shortestColumn;
  masonry.tallestColumn = tallestColumn;

  return masonry;
});

/**
 * Handler for screen resize events. Updates component state with relevant viewport calculations.
 */
const handleResize = () =>
  nextTick(
    () =>
      (viewportHeight.value = window.innerHeight || document.documentElement.clientHeight) &&
      (columnWidth.value =
        // To properly fill the container, the total gutter width needs to be used instead of each item's spacing.
        ((root.value?.clientWidth ?? columnWidth.value) -
          // total gutter width
          props.itemHorizontalSpacing * (props.columnCount - 1)) /
        props.columnCount),
  );

/**
 * Handler for scroll events. Updates the component state for scroll height.
 */
const handleScroll = () => {
  scrollHeight.value = document.documentElement.scrollTop;
};

watch(
  // This is necessary as the <c-tab-item /> is programmatically set to `display: none` by Vuetify.
  // Without this the handleResize() incorrectly calculates as this child element is not "visible".
  () => props.isVisible,
  (nevVal, prevVal) => {
    if (prevVal === false && nevVal === true) {
      setTimeout(handleResize, resizeDelayOnTransition);
    }
  },
);

watch(
  computed(() => breakpoint.name),
  handleResize,
);

return { __sfc: true,props, store, breakpoint, root, iPhoneXHeight, iPhoneXWidth, resizeDelayOnTransition, hasHydrated, scrollHeight, columnWidth, viewportHeight, virtualMasonry, flatMasonry, shortestColumnFunc, masonry, handleResize, handleScroll }
}

})